import type { EventInput } from '@fullcalendar/core';
import { formatCalendarDay, toCalendarDay } from '@adornis/datetime/calendarDay.js';
import { addDays, format } from 'date-fns';
import { Abwesenheit } from '../../db/abwesenheit.js';
import { AbsenceApprovalStatus } from '../../db/enums/status.js';
import { translationDict } from '../../db/translation-dict.js';
import type { InterviewSlot } from '../../sus-entry-form/interviews/entities/InterviewSlot.js';

const requestedStyles = {
  [AbsenceApprovalStatus.REQUESTED]: {
    backgroundColor: 'var(--color-warning)',
    borderColor: 'var(--color-warning)',
  },
  [AbsenceApprovalStatus.APPROVED]: {
    backgroundColor: 'var(--color-success)',
    borderColor: 'var(--color-success)',
  },
  [AbsenceApprovalStatus.DENIED]: {
    backgroundColor: 'var(--color-error)',
    borderColor: 'var(--color-error)',
  },
};

function camelCaseToWhitespace(str: string) {
  return str.replace(/([A-Z])/g, ' $1');
}

export function mapEntitiesToCalenderEvents(entities: Array<Abwesenheit | InterviewSlot[]>) {
  // disabled complexity as it is just mapping and 99% null coalescing
  // eslint-disable-next-line complexity
  return entities.map(entity => {
    let start: Date | null = null;
    let end: Date | null = null;
    let title: string | null = null;
    let allDay: boolean | null = null;
    let backgroundColor: string | null = null;
    let borderColor: string | null = null;

    if (entity instanceof Abwesenheit) {
      start = entity.start ?? null;
      end = entity.ende ?? null;
      title = translationDict._class[entity._class][0] ?? camelCaseToWhitespace(entity._class);
      allDay = entity.ganztaegig;
      backgroundColor = requestedStyles[entity.genehmigungsStatus].backgroundColor;
      borderColor = requestedStyles[entity.genehmigungsStatus].borderColor;
    } else {
      const interviewSlot = entity[0];
      const isFull = entity.every(slot => slot.interviewerID);
      if (!interviewSlot) return null;
      start = interviewSlot.startDate ?? null;
      end = interviewSlot.endDate ?? null;
      title =
        'SUS-Gespräch ' +
        (interviewSlot.startDate ? format(interviewSlot.startDate, 'HH:mm') : '') +
        ' - ' +
        (interviewSlot.endDate ? format(interviewSlot.endDate, 'HH:mm') : '');
      allDay = false;
      backgroundColor = isFull ? 'var(--color-error)' : 'var(--color-success)';
      borderColor = isFull ? 'var(--color-error)' : 'var(--color-success)';
    }

    /**
     * Abwesenheiten are calendar days: `start` and `ende` are both inclusive and stored canonically
     * at 00:00Z. Handed to FullCalendar as a `Date` they would be read in the browser's zone and
     * show up a day early west of Greenwich, so they go over as `yyyy-MM-dd` strings, which
     * FullCalendar treats as zone-less days. The end additionally becomes exclusive by moving to
     * the following day; `addDays` keeps the browser-local wall time and drifts across a DST
     * boundary, so the result is normalized back onto the canonical grid.
     *
     * Interview slots are real instants and are passed through unchanged.
     */
    const isAbsence = entity instanceof Abwesenheit;
    const startDay = isAbsence ? toCalendarDay(start) : null;
    const inclusiveEndDay = isAbsence ? toCalendarDay(end) : null;
    const exclusiveEndDay = inclusiveEndDay ? toCalendarDay(addDays(inclusiveEndDay, 1)) : null;

    const eventInput: EventInput = {
      start: startDay ? formatCalendarDay(startDay, 'yyyy-MM-dd') : (start ?? undefined),
      end: exclusiveEndDay ? formatCalendarDay(exclusiveEndDay, 'yyyy-MM-dd') : (end ?? undefined),
      title,
      allDay,
      backgroundColor,
      borderColor,
      extendedProps: {
        entity,
      },
    };

    return eventInput;
  });
}
